import Column from '@components/column'
import Row from '@components/row'
import Typography from '@components/typography'
import SectionHeading from '@commons/headings/section'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helper } from '@utils'
import { Formik, Form } from 'formik'
import { useEffect, useState, Fragment } from 'react'
import CardHeading from '@commons/headings/card'
import { InstituteHealthcareSchema } from '@validations'
import {
  StyleFormGrid,
  StyleImageRow,
  StyleWorkWrapper,
  StyleWorkItem,
} from './style'
import Fields from '@components/fields'
import Button from '@components/button'
import { Category, InstituteHealthService } from '@apis'
import { useSelector } from 'react-redux'
import Alert from '@commons/popups/alert'

const InstituteHealthServiceFormPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { slug } = useParams()
  const { data } = useSelector((state: any) => state.instituteHealthService)
  const healthService: any = !!state?.id ? state : data
  const [initialValues, setInitialValues] = useState({
    name: '',
    categoryId: '',
    subCategoryId: '',
    description: '',
    images: ['', '', '', ''],
    infos: {
      availability: '',
      pricePerVisit: '',
      includes: [''],
      status: true,
    },
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const id = Helper.getIdFromSlug(slug)
  const [deleteInfo, setDeleteInfo] = useState({
    visible: false,
    loading: false,
  })

  const onClickDelete = () => {
    setDeleteInfo({
      visible: true,
      loading: false,
    })
  }
  const removeData = async () => {
    setDeleteInfo({
      ...deleteInfo,
      loading: true,
    })
    await InstituteHealthService.remove({
      id,
    })
      .then(() => {
        navigate(`/institution/lab-tests`)
      })
      .catch(() => {})
    setDeleteInfo({
      ...deleteInfo,
      visible: false,
      loading: false,
    })
  }

  const fetchCategories = async () => {
    await Category.list({
      params: {
        type: 'Healthcare',
      },
    })
      .then((resp) => {
        setCategories(resp)
      })
      .catch(() => {})
  }

  const fetchSubCategories = async (parentId: number) => {
    await Category.list({
      params: {
        type: 'HealthcareSubCategory',
        parentId,
      },
    })
      .then((resp) => {
        setSubCategories(resp)
      })
      .catch(() => {})
  }

  const fetchData = async () => {
    await InstituteHealthService.find({
      id: id,
    }).catch(() => {})
  }

  const submitHandler = async (values) => {
    setLoading(true)
    const payload: any = {
      name: values?.name,
      description: values?.description,
      categoryId: values?.categoryId,
      subCategoryId: values?.subCategoryId,
      infos: values?.infos,
    }
    if (!!values?.images?.length) {
      const images = values?.images
        ?.filter((image: any) => !!image && !!image?.base64)
        ?.map((image) => {
          const item: any = {
            base64: image?.base64,
            extension: image?.extension,
          }
          if (image?.id) {
            item.id = image.id
          }
          return item
        })
      if (!!images?.length) {
        payload.images = images
      }
    }
    if (!!healthService?.id) {
      await InstituteHealthService.update({
        data: payload,
        id,
      })
        .then(() => {
          navigate('/institution/health-services')
        })
        .catch(() => {})
    } else {
      await InstituteHealthService.create({
        data: payload,
      })
        .then(() => {
          navigate('/institution/health-services')
        })
        .catch(() => {})
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchCategories()
  }, [])
  useEffect(() => {
    if (!state?.id && !!id) {
      fetchData()
    }
    if (!!healthService?.id) {
      fetchSubCategories(healthService?.category?.id)
      setInitialValues({
        ...initialValues,
        name: healthService?.name,
        description: healthService?.description,
        categoryId: healthService?.category?.id,
        subCategoryId: healthService?.subCategory?.id,
        images: Array(4)
          .fill('')
          ?.map((_, index) => {
            return healthService?.images[index]?.image || ''
          }),
        infos: healthService?.infos,
      })
    }
  }, [id, healthService?.id])
  return (
    <Column rowGap={20}>
      <SectionHeading
        title={`${!!id ? 'Update' : 'Create'} a Healthcare Service`}
        tc="primary"
      />
      <Formik
        initialValues={initialValues}
        onSubmit={submitHandler}
        validationSchema={InstituteHealthcareSchema.validationSchema}
        enableReinitialize
      >
        {({ handleSubmit, setFieldValue, values }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Column rowGap={20}>
                {!!healthService?.id && (
                  <Row
                    bg="white"
                    styleRow="border-radius:12px;padding:15px 20px;"
                    rowGap={20}
                    alignItems="center"
                  >
                    <Fields.Switch
                      name="infos.status"
                      label="Lab Test Status"
                      styleWrapper="display:flex;flex-direction:row;column-gap:10px;"
                    />
                    <Button
                      title="Delete"
                      bg="danger"
                      outline
                      onClick={onClickDelete}
                    />
                  </Row>
                )}
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <CardHeading title="Service Details" />
                  <StyleFormGrid>
                    <Fields.Input
                      name="name"
                      label="Healthcare Service Name"
                      placeholder="Healthcare Service Name"
                    />
                    <Fields.DropDown
                      name="categoryId"
                      options={categories}
                      titleAsKey="name"
                      valueAsKey="id"
                      label="Category"
                      placeholder="Select Category"
                      onChange={(value) => {
                        setFieldValue('categoryId', value)
                        fetchSubCategories(value)
                      }}
                    />
                    <Fields.DropDown
                      name="subCategoryId"
                      options={subCategories}
                      titleAsKey="name"
                      valueAsKey="id"
                      label="Sub Category"
                      placeholder="Select Sub Category"
                    />
                    <Fields.DropDown
                      label="Availability"
                      name="infos.availability"
                      placeholder="Select Availability"
                      options={[
                        {
                          label: 'Home',
                        },
                        {
                          label: 'Clinic',
                        },
                        {
                          label: 'Both',
                        },
                      ]}
                      titleAsKey="label"
                      valueAsKey="label"
                    />
                    <Fields.Input
                      type="number"
                      name="infos.pricePerVisit"
                      label="Price Per Visit"
                      placeholder="Price Per Visit"
                    />
                  </StyleFormGrid>
                  <Fields.TextArea
                    name="infos.additionalNote"
                    label="Additional Note"
                    placeholder="Enter additional note/ eg. 10-12 fasting required for this Test"
                    rows={3}
                  />
                  <StyleImageRow>
                    {values?.images?.map((_, index: any) => {
                      return (
                        <Fields.Upload
                          name={`images[${index}]`}
                          varient="drag"
                          label="Healthcare Image"
                          placeholder="Upload Image"
                          styleWrapper="width:275px;"
                          size={150}
                          key={index}
                          onChange={(file) => {
                            const item: any = {
                              base64: file?.base64,
                              extension: file?.extension,
                              info: file?.info,
                            }
                            if (!!id) {
                              if (healthService?.images?.[index]) {
                                item.id = healthService?.images[index]?.id
                              }
                            }

                            setFieldValue(`images[${index}]`, item)
                          }}
                        />
                      )
                    })}
                  </StyleImageRow>
                </Column>
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <Fields.TextArea
                    name="description"
                    label="Service Description"
                    placeholder="Enter Service Description"
                    rows={3}
                  />
                </Column>
                <Column
                  bg="white"
                  styleColumn="border-radius:12px;padding:15px 20px;"
                  rowGap={20}
                >
                  <Typography
                    content="Services Included In"
                    size={14}
                    tc="primary"
                    weight={500}
                  />
                  <StyleWorkWrapper>
                    {values?.infos?.includes?.map((_, index) => {
                      return (
                        <Fragment key={index}>
                          <StyleWorkItem>
                            <Fields.Input
                              name={`infos.includes[${index}]`}
                              placeholder="Enter Include"
                              styleInput="
                                border:0px;
                              "
                              onChange={(text) => {
                                setFieldValue(`infos.includes[${index}]`, text)
                                if (index != 0) {
                                  if (!text) {
                                    setFieldValue(
                                      'infos.includes',
                                      values?.infos?.includes?.filter(
                                        (_, i) => i != index,
                                      ),
                                    )
                                  }
                                }
                              }}
                              onKeyDown={(e: any) => {
                                if (e.key == 'Enter') {
                                  e.preventDefault()
                                  setFieldValue('infos.includes', [
                                    ...values?.infos?.includes,
                                    '',
                                  ])
                                }
                              }}
                            />
                          </StyleWorkItem>
                        </Fragment>
                      )
                    })}
                  </StyleWorkWrapper>
                </Column>
                <Row justifyContent="flex-end">
                  <Button
                    title={!!id ? 'Update Service' : 'Add Service'}
                    height={45}
                    loading={loading}
                    type={'submit'}
                  />
                </Row>
              </Column>
            </Form>
          )
        }}
      </Formik>
      <Alert
        visible={deleteInfo?.visible}
        loading={deleteInfo?.loading}
        onClickOk={removeData}
        onClickClose={() => {
          setDeleteInfo({
            visible: false,
            loading: false,
          })
        }}
      />
    </Column>
  )
}

export default InstituteHealthServiceFormPage
